/**
 * Content-script-safe lazy loading for heavy, rarely-used-at-startup libraries.
 *
 * ── Why this exists ────────────────────────────────────────────────────────────
 * The content bundle (app.js) eagerly ships xlsx-js-style (~900KB), pdfjs
 * (~1MB+) and mammoth, even though they are only used behind occasional user
 * actions (Excel export / campaign-log export / AI-assistant file attachment).
 *
 * Webpack's DEFAULT async-chunk loader can't help inside a WhatsApp Web content
 * script: it appends a `<script src=chrome-extension://…>` that executes in the
 * PAGE's MAIN world, where WhatsApp's CSP blocks chrome-extension: URLs. BUT a
 * native dynamic `import()` from an MV3 content script (ISOLATED world) CAN load
 * a chrome-extension:// module URL that is listed in `web_accessible_resources`.
 * That is exactly what `loadFromWar` does below.
 *
 * The ESM bundles it loads (`dist/lazy/*.js`) are produced by a dedicated
 * compiler — see webpack.extension.lazy.js — and the resources are whitelisted
 * in every manifest's `web_accessible_resources` (dev/staging/prod).
 *
 * ── The fallback (safety net) ──────────────────────────────────────────────────
 * Each loader first tries the lazy WAR module; on ANY failure it falls back to a
 * webpack-bundled copy (`webpackMode: "eager"`, so it stays inlined in app.js and
 * never becomes a CSP-blocked async chunk). This means a lazy-load failure on
 * WhatsApp Web degrades to *today's* behavior instead of breaking the feature.
 *
 * ── Removing the fallback after QA (the single switch) ─────────────────────────
 * While the fallback is present, app.js still bundles these libs, so there is NO
 * bundle-size win yet — the lazy path is only being *exercised* for QA. Once the
 * coordinator confirms the lazy path loads correctly on WhatsApp Web, flip the
 * compile-time flag `__BT_LAZY_NO_FALLBACK__` from `false` to `true` in
 * webpack.extension.common.js (DefinePlugin) and restart the watcher. Webpack
 * then dead-code-eliminates the `if (!__BT_LAZY_NO_FALLBACK__) { … }` blocks
 * below (the `if (false)` branch is dropped at parse time, in every build mode),
 * which drops the eager `import()`s and finally removes the libs from app.js.
 *
 * ── Lazy COMPONENT-tree bundles (React-identity bridge) ────────────────────────
 * The same isolated-world native-import mechanism also loads lazy bundles that
 * render React COMPONENTS (the calendar overlay is the first). Those cannot ship
 * their own React — hooks/context only work with ONE React instance — so BEFORE
 * importing such a bundle the loader publishes app.js's own React copies on
 * `globalThis.__BT_REACT__`, and the bundle's react/react-dom/react-is/react-redux
 * imports are aliased (webpack.extension.lazy.js) to shim modules that re-export
 * from that global. The component tree then renders inside the host's existing
 * React tree, so theme/redux context flows through React itself.
 */

// Statically imported here so they are app.js's OWN singletons (already present
// in app.js — zero added weight). Published on globalThis.__BT_REACT__ for the
// lazy component bundles' shim modules to re-export. See shims/*.js.
import React from "react";
import ReactDOM from "react-dom";
import * as jsxRuntime from "react/jsx-runtime";
import * as reactIs from "react-is";
import * as reactRedux from "react-redux";

// The live-binding holder the slate editor components read from. loadSlateLib
// (below) populates it via setSlateRuntime. Importing this pulls in NO slate
// (slate-runtime.ts has zero slate imports — only `let` bindings).
import { setSlateRuntime } from "./slate-runtime";

// Injected by webpack DefinePlugin (webpack.extension.common.js). `false` while
// the bundled fallback is retained; set to `true` after live QA to strip it.
declare const __BT_LAZY_NO_FALLBACK__: boolean;

// Injected by webpack DefinePlugin (webpack.extension.common.js). Mirrors the
// pattern above but for the lazy COMPONENT-tree bundles (calendar overlay, …).
//   true  (default): keep the eager react-big-calendar fallback bundled in app.js
//     so a lazy-load failure on WhatsApp Web degrades to today's behavior. NO
//     bundle win yet — the lazy path is only being exercised for QA.
//   false: dead-code-eliminate the eager fallback import()s so react-big-calendar
//     (+ its dnd addon) finally leave app.js. Flip to false (and restart the
//     watcher) ONLY after the coordinator confirms the calendar overlay loads via
//     the native lazy/calendar.js path on WhatsApp Web.
declare const __BT_CALENDAR_LAZY_FALLBACK__: boolean;

// Injected by webpack DefinePlugin (webpack.extension.common.js). Same pattern
// as the calendar flag but for the react-date-range picker lazy bundle
// (bt-timeline — see loadDateRangeLib below).
//   true  (default): keep the eager react-date-range fallback bundled in app.js
//     so a lazy-load failure on WhatsApp Web degrades to today's behavior. NO
//     bundle win yet — the lazy path is only being exercised for QA.
//   false: dead-code-eliminate the eager fallback import()s so react-date-range
//     finally leaves app.js. Flip to false (and restart the watcher) ONLY after
//     the coordinator confirms the date-range picker opens via the native
//     lazy/date-range.js path on WhatsApp Web.
declare const __BT_DATERANGE_LAZY_FALLBACK__: boolean;

// Injected by webpack DefinePlugin (webpack.extension.common.js) + craco. Same
// pattern as the calendar flag, for the lazy lexical editor bundle
// (LexicalEditorCore — the whole lexical runtime: lexical + @lexical/* + nodes +
// plugins + serializer).
//   true  (default): keep an eager webpack-bundled copy of the editor core in
//     app.js so a lazy/lexical.js load failure on WhatsApp Web — and the website,
//     which has no chrome runtime — degrades to today's behavior. NO app.js win
//     yet (QA only).
//   false: dead-code-eliminate the eager fallback import() so lexical finally
//     leaves app.js. Flip to false (and restart the watcher) ONLY after the
//     coordinator confirms the message editor loads via the native
//     lazy/lexical.js path on WhatsApp Web. NEVER flip false for the website
//     (craco keeps it true — no WAR path there).
declare const __BT_LEXICAL_LAZY_FALLBACK__: boolean;

// Injected by webpack DefinePlugin (webpack.extension.common.js) + craco. Same
// pattern as the calendar/date-range/lexical flags but for the slate editor lib
// lazy bundle (slate + slate-react + slate-history — see loadSlateLib below).
//   true  (default): keep an eager webpack-bundled slate copy in app.js so a
//     lazy/slate.js load failure on WhatsApp Web — and the website, which has no
//     chrome runtime — degrades to today's behavior. NO app.js win yet (QA only).
//   false: dead-code-eliminate the eager fallback import() so slate (+
//     slate-react + slate-history) finally leave app.js. Flip to false (and
//     restart the watcher) ONLY after the coordinator confirms the task-notes /
//     reminder editor opens via the native lazy/slate.js path on WhatsApp Web.
//     NEVER flip false for the website (craco keeps it true — no WAR path there).
declare const __BT_SLATE_LAZY_FALLBACK__: boolean;

// Injected by webpack DefinePlugin (webpack.extension.common.js) + craco. Same
// pattern as the calendar/date-range flags but for the react-world-flags <Flag/>
// component lazy bundle (see loadWorldFlagsLib below). react-world-flags is a
// single ~3.7MB module (the component + an inline all-countries SVG flag map)
// used by the always-visible top-header language switcher + the transcription
// language picker, so today it is parsed at content-script startup.
//   true  (default): keep an eager webpack-bundled react-world-flags copy in
//     app.js so a lazy/world-flags.js load failure on WhatsApp Web — and the
//     website, which has no chrome runtime — degrades to today's behavior. NO
//     app.js win yet (QA only).
//   false: dead-code-eliminate the eager fallback import() so react-world-flags
//     finally leaves app.js. Flip to false (extension only; craco stays true)
//     ONLY after the coordinator confirms the language switcher + transcription
//     language picker flags render via the native lazy/world-flags.js path on
//     WhatsApp Web.
declare const __BT_WORLDFLAGS_LAZY_FALLBACK__: boolean;

// Injected by webpack DefinePlugin (webpack.extension.common.js) + craco. Same
// pattern as the world-flags flag but for the lottie-react <Lottie/> component
// lazy bundle (see loadLottieLib below). lottie-react's only heavy dependency is
// lottie-web (~250KB, the Bodymovin animation engine); today it is parsed at
// content-script startup even though every <Lottie/> in the app is a decorative
// empty-state / loader / checkmark shown only behind a user interaction.
//   true  (default): keep an eager webpack-bundled lottie-react copy in app.js
//     so a lazy/lottie.js load failure on WhatsApp Web — and the website, which
//     has no chrome runtime — degrades to today's behavior. NO app.js win yet
//     (QA only).
//   false: dead-code-eliminate the eager fallback import() so lottie-react
//     (+ lottie-web) finally leave app.js. Flip to false (extension only; craco
//     stays true) ONLY after the coordinator confirms a lottie animation (e.g.
//     an empty tasks/contact list, or the trial-activate modal) renders via the
//     native lazy/lottie.js path on WhatsApp Web.
declare const __BT_LOTTIE_LAZY_FALLBACK__: boolean;

// Injected by webpack DefinePlugin (webpack.extension.common.js) + craco. Same
// pattern as the world-flags/lottie flags but for the @cyntler/react-doc-viewer
// document-preview component lazy bundle (see loadDocViewer below). react-doc-viewer
// drags in react-pdf (pdfjs) + styled-components + papaparse + mustache + ajv
// (~228KB) but only renders inside the task media-manager dialog (opened behind a
// user interaction).
//   true  (default): keep an eager webpack-bundled react-doc-viewer copy in app.js
//     so a lazy/doc-viewer.js load failure on WhatsApp Web — and the website, which
//     has no chrome runtime — degrades to today's behavior. NO app.js win yet (QA
//     only).
//   false: dead-code-eliminate the eager fallback import() so react-doc-viewer
//     finally leaves app.js. Flip to false (extension only; craco stays true) ONLY
//     after the coordinator confirms the media-manager document preview renders via
//     the native lazy/doc-viewer.js path on WhatsApp Web.
declare const __BT_DOCVIEWER_LAZY_FALLBACK__: boolean;

// Injected by webpack DefinePlugin (webpack.extension.common.js) + craco. Same
// pattern as the world-flags/lottie flags but for the emoji-picker-react
// <EmojiPicker/> component lazy bundle (see loadEmojiPicker below). The picker
// (+ flairup + inline emoji data, ~161KB) renders in the message composer, chatbot
// editor, task/board icon pickers, team-inbox input and labels — always inside a
// Menu/Popover opened by clicking an emoji button.
//   true  (default): keep an eager webpack-bundled emoji-picker-react copy in
//     app.js so a lazy/emoji-picker.js load failure on WhatsApp Web — and the
//     website, which has no chrome runtime — degrades to today's behavior. NO
//     app.js win yet (QA only).
//   false: dead-code-eliminate the eager fallback import() so emoji-picker-react
//     finally leaves app.js. Flip to false (extension only; craco stays true) ONLY
//     after the coordinator confirms the emoji picker opens via the native
//     lazy/emoji-picker.js path on WhatsApp Web.
declare const __BT_EMOJI_LAZY_FALLBACK__: boolean;

/** Memoize a loader so each lib is fetched/parsed at most once per document. */
function once<T>(fn: () => Promise<T>): () => Promise<T> {
  let cached: Promise<T> | undefined;
  return () => (cached ??= fn());
}

/**
 * Load a web_accessible_resource ES module from the extension origin via a
 * native isolated-world dynamic import (CSP-safe on WhatsApp Web).
 * `webpackIgnore` keeps webpack from rewriting this into a chunk request.
 */
async function loadFromWar(resource: string): Promise<any> {
  const url = chrome.runtime.getURL(resource);
  return import(/* webpackIgnore: true */ url);
}

/**
 * xlsx-js-style (SheetJS + cell styling). Used by the Excel/campaign-log export
 * flows and the AI-assistant spreadsheet text extraction.
 * Returns the XLSX object (`.utils`, `.read`, `.writeFile`, …).
 */
export const loadXlsx = once(async (): Promise<typeof import("xlsx-js-style")> => {
  try {
    const mod = await loadFromWar("lazy/xlsx.js");
    return (mod?.default ?? mod) as typeof import("xlsx-js-style");
  } catch (e) {
    if (!__BT_LAZY_NO_FALLBACK__) {
      console.warn("[lazy-lib] lazy/xlsx.js load failed — using bundled fallback", e);
      const mod: any = await import(/* webpackMode: "eager" */ "xlsx-js-style");
      return (mod?.default ?? mod) as typeof import("xlsx-js-style");
    }
    throw e;
  }
});

/**
 * mammoth (DOCX -> text/HTML). Used by AI-assistant file attachment.
 * Returns the mammoth object (`.extractRawText`, …).
 */
export const loadMammoth = once(async (): Promise<typeof import("mammoth")> => {
  try {
    const mod = await loadFromWar("lazy/mammoth.js");
    return (mod?.default ?? mod) as typeof import("mammoth");
  } catch (e) {
    if (!__BT_LAZY_NO_FALLBACK__) {
      console.warn("[lazy-lib] lazy/mammoth.js load failed — using bundled fallback", e);
      const mod: any = await import(/* webpackMode: "eager" */ "mammoth");
      return (mod?.default ?? mod) as typeof import("mammoth");
    }
    throw e;
  }
});

/**
 * jszip (create/read ZIP archives in the browser). Used by the task file
 * pickers and media manager to bundle multiple files into a single download.zip.
 * Returns the JSZip constructor (`new JSZip()`, `.loadAsync`, instances expose
 * `.file` / `.generateAsync`).
 */
export const loadJszip = once(async (): Promise<typeof import("jszip")> => {
  try {
    const mod = await loadFromWar("lazy/jszip.js");
    return (mod?.default ?? mod) as typeof import("jszip");
  } catch (e) {
    if (!__BT_LAZY_NO_FALLBACK__) {
      console.warn("[lazy-lib] lazy/jszip.js load failed — using bundled fallback", e);
      const mod: any = await import(/* webpackMode: "eager" */ "jszip");
      return (mod?.default ?? mod) as typeof import("jszip");
    }
    throw e;
  }
});

/**
 * pdfjs (PDF -> text). Used by AI-assistant file attachment.
 * lazy/pdfjs.js is pdfjs-dist's prebuilt browser ESM (named exports), so the
 * namespace is returned as-is (do NOT unwrap `.default`).
 */
export const loadPdfjs = once(async (): Promise<typeof import("pdfjs-dist")> => {
  try {
    return (await loadFromWar("lazy/pdfjs.js")) as typeof import("pdfjs-dist");
  } catch (e) {
    if (!__BT_LAZY_NO_FALLBACK__) {
      console.warn("[lazy-lib] lazy/pdfjs.js load failed — using bundled fallback", e);
      return (await import(/* webpackMode: "eager" */ "pdfjs-dist")) as typeof import("pdfjs-dist");
    }
    throw e;
  }
});

// ── Lazy COMPONENT-tree infrastructure (reusable) ──────────────────────────────

/**
 * Publish app.js's own React singletons on globalThis.__BT_REACT__ so the shim
 * modules aliased into a lazy component bundle re-export the SAME React instance.
 * Idempotent — safe to call before every component-bundle import.
 */
let reactBridgeInstalled = false;
function installReactBridge(): void {
  if (reactBridgeInstalled) return;
  (globalThis as any).__BT_REACT__ = {
    React,
    ReactDOM,
    jsxRuntime,
    reactIs,
    reactRedux,
  };
  reactBridgeInstalled = true;
}

/**
 * Load a lazy COMPONENT-tree bundle (`dist/lazy/<name>.js`) from the extension
 * origin via the CSP-safe isolated-world native import, after installing the
 * React-identity bridge so the bundle shares app.js's React. Per-bundle once()
 * memo: each named bundle is fetched/parsed at most once per document.
 *
 * This is the ONE-TIME mechanism future feature trees reuse — add a lazy entry +
 * a thin loader (like loadCalendarLib below) and the bridge/shims already apply.
 */
const componentBundleLoaders = new Map<string, () => Promise<any>>();
export function loadLazyComponentBundle(name: string): Promise<any> {
  let loader = componentBundleLoaders.get(name);
  if (!loader) {
    loader = once(async () => {
      installReactBridge();
      return loadFromWar(`lazy/${name}.js`);
    });
    componentBundleLoaders.set(name, loader);
  }
  return loader();
}

/** The react-big-calendar lib surface exposed by lazy/calendar.js. */
export interface CalendarLib {
  Calendar: any;
  momentLocalizer: (m: any) => any;
  withDragAndDrop: (calendar: any) => any;
}

/**
 * react-big-calendar (calendar grid engine + drag-and-drop addon), loaded from
 * the lazy/calendar.js component bundle sharing app.js's React. Used by the
 * calendar overlay (see src/common/views/calendar/lazy-dnd-calendar.tsx).
 *
 * While __BT_CALENDAR_LAZY_FALLBACK__ is true, a lazy-load failure falls back to
 * an eager (webpackMode:"eager") bundled copy — so react-big-calendar stays in
 * app.js and the overlay degrades to today's behavior. Flip the flag to false
 * after live QA to strip that fallback and drop the lib from app.js.
 */
export const loadCalendarLib = once(async (): Promise<CalendarLib> => {
  try {
    const mod: any = await loadLazyComponentBundle("calendar");
    return {
      Calendar: mod.Calendar,
      momentLocalizer: mod.momentLocalizer,
      withDragAndDrop: mod.withDragAndDrop ?? mod.default,
    };
  } catch (e) {
    if (__BT_CALENDAR_LAZY_FALLBACK__) {
      console.warn("[lazy-lib] lazy/calendar.js load failed — using bundled fallback", e);
      const rbc: any = await import(/* webpackMode: "eager" */ "react-big-calendar");
      const dnd: any = await import(/* webpackMode: "eager" */ "react-big-calendar/lib/addons/dragAndDrop");
      return {
        Calendar: rbc.Calendar,
        momentLocalizer: rbc.momentLocalizer,
        withDragAndDrop: dnd.default ?? dnd,
      };
    }
    throw e;
  }
});

/** The react-date-range lib surface exposed by lazy/date-range.js. */
export interface DateRangeLib {
  DateRange: any;
  DateRangePicker: any;
  Calendar: any;
}

/**
 * react-date-range (date-range picker), loaded from the lazy/date-range.js
 * component bundle sharing app.js's React. Used by bt-timeline (see
 * src/common/components/bt-timeline/lazy-date-range.tsx). Its two CSS files are
 * link-injected via the manifests (content_scripts.css) — unaffected here.
 *
 * While __BT_DATERANGE_LAZY_FALLBACK__ is true, a lazy-load failure falls back
 * to an eager (webpackMode:"eager") bundled copy — so react-date-range stays in
 * app.js and the picker degrades to today's behavior. Flip the flag to false
 * after live QA to strip that fallback and drop the lib from app.js.
 */
export const loadDateRangeLib = once(async (): Promise<DateRangeLib> => {
  try {
    const mod: any = await loadLazyComponentBundle("date-range");
    return {
      DateRange: mod.DateRange,
      DateRangePicker: mod.DateRangePicker,
      Calendar: mod.Calendar,
    };
  } catch (e) {
    if (__BT_DATERANGE_LAZY_FALLBACK__) {
      console.warn("[lazy-lib] lazy/date-range.js load failed — using bundled fallback", e);
      const rdr: any = await import(/* webpackMode: "eager" */ "react-date-range");
      return {
        DateRange: rdr.DateRange,
        DateRangePicker: rdr.DateRangePicker,
        Calendar: rdr.Calendar,
      };
    }
    throw e;
  }
});

/** The lexical editor surface exposed by lazy/lexical.js. */
export interface LexicalEditorLib {
  /** The coupling-free forwardRef editor component (LexicalEditorCore). */
  LexicalEditorCore: any;
}

/**
 * The WhatsApp message editor (lexical + @lexical/* + every node subclass, plugin
 * and the markdown serializer), loaded from the lazy/lexical.js component bundle
 * sharing app.js's React via the __BT_REACT__ bridge. Consumed by the app-side
 * host src/common/components/message-editor/lexical/LexicalMessageEditor.tsx.
 *
 * The whole editor implementation ships in the bundle (not just the lexical npm
 * packages) because lexical registers LexicalNode subclasses and relies on
 * `instanceof` across the document model — all lexical code must resolve to ONE
 * copy. The app-graph couplings (Redux dispatch, i18n placeholder, @mention
 * candidates) stay in app.js and are passed into the core as props.
 *
 * While __BT_LEXICAL_LAZY_FALLBACK__ is true, a lazy-load failure (or the website,
 * which has no `chrome` runtime) falls back to an eager (webpackMode:"eager")
 * bundled copy — so lexical stays in app.js and the editor degrades to today's
 * behavior. Flip the flag to false (extension only) after live QA to strip that
 * fallback and drop lexical from app.js.
 */
export const loadLexicalEditor = once(async (): Promise<LexicalEditorLib> => {
  try {
    const mod: any = await loadLazyComponentBundle("lexical");
    return { LexicalEditorCore: mod.LexicalEditorCore ?? mod.default };
  } catch (e) {
    if (__BT_LEXICAL_LAZY_FALLBACK__) {
      console.warn("[lazy-lib] lazy/lexical.js load failed — using bundled fallback", e);
      const mod: any = await import(
        /* webpackMode: "eager" */ "@/common/components/message-editor/lexical/LexicalEditorCore"
      );
      return { LexicalEditorCore: mod.default ?? mod.LexicalEditorCore };
    }
    throw e;
  }
});

/**
 * The slate editor lib (slate core + slate-react + slate-history), loaded from
 * the lazy/slate.js component bundle sharing app.js's React via the __BT_REACT__
 * bridge. Unlike lexical, the slate EDITOR COMPONENTS stay in app.js (they are
 * deeply coupled to MUI / redux / i18next — moving them into the bundle would
 * duplicate MUI and sever the theme/store contexts, same scope rule as the
 * calendar split). Instead only the lib layer ships in the bundle, and the app
 * editor components consume its primitives through the live-binding holder
 * src/extension/utils/slate-runtime.ts, which this loader populates via
 * setSlateRuntime BEFORE the React.lazy boundary (lazy-slate.tsx) renders them.
 *
 * ONE-copy guarantee: because this loader is memoized (once()), exactly one
 * slate + slate-react copy ever populates the holder per document, so
 * slate-react's WeakMaps and React context have a single consistent identity
 * (Editor.isEditor / Element.isElement / DOM<->node lookups stay coherent).
 *
 * While __BT_SLATE_LAZY_FALLBACK__ is true, a lazy-load failure (or the website,
 * which has no `chrome` runtime) falls back to an eager (webpackMode:"eager")
 * bundled copy — so slate stays in app.js and the editor degrades to today's
 * behavior. Flip the flag to false (extension only) after live QA to strip that
 * fallback and drop slate from app.js.
 */
export const loadSlateLib = once(async (): Promise<any> => {
  try {
    const mod: any = await loadLazyComponentBundle("slate");
    setSlateRuntime(mod);
    return mod;
  } catch (e) {
    if (__BT_SLATE_LAZY_FALLBACK__) {
      console.warn("[lazy-lib] lazy/slate.js load failed — using bundled fallback", e);
      const slate: any = await import(/* webpackMode: "eager" */ "slate");
      const slateReact: any = await import(/* webpackMode: "eager" */ "slate-react");
      const slateHistory: any = await import(/* webpackMode: "eager" */ "slate-history");
      // Merge the three namespaces into the single shape setSlateRuntime expects
      // (matches the WAR bundle, which re-exports all names from one module).
      const merged: any = { ...slate, ...slateReact, ...slateHistory };
      setSlateRuntime(merged);
      return merged;
    }
    throw e;
  }
});

/**
 * react-world-flags <Flag/> component (+ its inline all-countries SVG map),
 * loaded from the lazy/world-flags.js component bundle sharing app.js's React
 * via the __BT_REACT__ bridge. Returns the Flag component (React.FC<FlagProps>).
 * Consumed through the stateful boundary
 * src/common/components/language-switcher/lazy-world-flag.tsx.
 *
 * While __BT_WORLDFLAGS_LAZY_FALLBACK__ is true, a lazy-load failure (or the
 * website, which has no `chrome` runtime) falls back to an eager
 * (webpackMode:"eager") bundled copy — so react-world-flags stays in app.js and
 * flags render exactly as today. Flip the flag to false (extension only) after
 * live QA to strip that fallback and drop react-world-flags from app.js.
 */
export const loadWorldFlagsLib = once(async (): Promise<any> => {
  try {
    const mod: any = await loadLazyComponentBundle("world-flags");
    // The lazy bundle's entry (world-flags-lazy.js) OWNS the interop and exports
    // a guaranteed-valid React wrapper component (WorldFlagComponent) that
    // forwards props to the real react-world-flags <Flag/>. So the consumer never
    // has to reason about the UMD default/namespace shape that broke R16 ("reading
    // 'code'"). Still tolerate .default (extra ESM interop hop) defensively.
    const Flag: any = mod?.WorldFlagComponent ?? mod?.default ?? mod?.Flag ?? mod;
    if (typeof console !== "undefined") {
      console.debug("[lazy-lib] world-flags resolved:", typeof Flag, Flag && Flag.name);
    }
    return Flag;
  } catch (e) {
    if (__BT_WORLDFLAGS_LAZY_FALLBACK__) {
      console.warn("[lazy-lib] lazy/world-flags.js load failed — using bundled fallback", e);
      const mod: any = await import(/* webpackMode: "eager" */ "react-world-flags");
      return mod.default ?? mod;
    }
    throw e;
  }
});

/**
 * lottie-react's <Lottie/> component (and, transitively, the lottie-web
 * Bodymovin animation engine), loaded from the lazy/lottie.js component bundle
 * sharing app.js's React via the __BT_REACT__ bridge. Returns the Lottie
 * component (React.FC<LottieComponentProps>). Consumed through the stateful
 * boundary src/common/components/lazy-lottie/lazy-lottie.tsx.
 *
 * While __BT_LOTTIE_LAZY_FALLBACK__ is true, a lazy-load failure (or the
 * website, which has no `chrome` runtime) falls back to an eager
 * (webpackMode:"eager") bundled copy — so lottie-react + lottie-web stay in
 * app.js and animations render exactly as today. Flip the flag to false
 * (extension only) after live QA to strip that fallback and drop lottie-web
 * from app.js.
 */
export const loadLottieLib = once(async (): Promise<any> => {
  try {
    const mod: any = await loadLazyComponentBundle("lottie");
    return mod.Lottie ?? mod.default;
  } catch (e) {
    if (__BT_LOTTIE_LAZY_FALLBACK__) {
      console.warn("[lazy-lib] lazy/lottie.js load failed — using bundled fallback", e);
      const mod: any = await import(/* webpackMode: "eager" */ "lottie-react");
      return mod.default ?? mod;
    }
    throw e;
  }
});

/**
 * @cyntler/react-doc-viewer's <DocViewer/> document-preview component (and its
 * react-pdf/pdfjs + styled-components + papaparse + mustache + ajv stack), loaded
 * from the lazy/doc-viewer.js component bundle sharing app.js's React via the
 * __BT_REACT__ bridge. Returns a wrapper component that already bakes in the
 * composed renderer list (built-in DocViewerRenderers + the custom webp renderer)
 * — see src/extension/lazy-entries/doc-viewer-lazy.js. Consumed through the
 * React.lazy boundary src/common/views/media-manager/lazy-doc-viewer.tsx.
 *
 * While __BT_DOCVIEWER_LAZY_FALLBACK__ is true, a lazy-load failure (or the
 * website, which has no `chrome` runtime) falls back to an eager
 * (webpackMode:"eager") bundled copy — so react-doc-viewer stays in app.js and the
 * preview renders exactly as today. Flip the flag to false (extension only) after
 * live QA to strip that fallback and drop react-doc-viewer from app.js.
 */
export const loadDocViewer = once(async (): Promise<any> => {
  try {
    const mod: any = await loadLazyComponentBundle("doc-viewer");
    return mod.DocViewerComponent ?? mod.default;
  } catch (e) {
    if (__BT_DOCVIEWER_LAZY_FALLBACK__) {
      console.warn("[lazy-lib] lazy/doc-viewer.js load failed — using bundled fallback", e);
      const mod: any = await import(/* webpackMode: "eager" */ "@cyntler/react-doc-viewer");
      const DocViewer: any = mod.default ?? mod.DocViewer;
      const DocViewerRenderers: any = mod.DocViewerRenderers ?? [];
      // Reproduce the entry's renderer composition (built-ins + custom webp) so the
      // fallback preserves webp preview behavior.
      const webpRenderer: any = ({ mainState: { currentDocument } }: any) => {
        if (!currentDocument) return null;
        return React.createElement(
          "div",
          { style: { margin: "auto", overflow: "hidden", height: "100%" }, id: "my-webp-renderer" },
          React.createElement("img", {
            id: "webp-img",
            width: "100%",
            height: "100%",
            alt: currentDocument?.fileName,
            src: currentDocument.fileData,
          })
        );
      };
      webpRenderer.fileTypes = ["webp", "image/webp"];
      webpRenderer.weight = 1;
      const pluginRenderers = [...DocViewerRenderers, webpRenderer];
      return function DocViewerFallback(props: any) {
        return React.createElement(DocViewer, { pluginRenderers, ...props });
      };
    }
    throw e;
  }
});

/**
 * emoji-picker-react's <EmojiPicker/> component (and its flairup CSS-in-JS runtime
 * + inline emoji data), loaded from the lazy/emoji-picker.js component bundle
 * sharing app.js's React via the __BT_REACT__ bridge. Returns the picker component
 * (a guaranteed-valid wrapper — see src/extension/lazy-entries/emoji-picker-lazy.js).
 * Consumed through the stateful boundary
 * src/common/components/lazy-emoji-picker/lazy-emoji-picker.tsx.
 *
 * While __BT_EMOJI_LAZY_FALLBACK__ is true, a lazy-load failure (or the website,
 * which has no `chrome` runtime) falls back to an eager (webpackMode:"eager")
 * bundled copy — so emoji-picker-react stays in app.js and the picker renders
 * exactly as today. Flip the flag to false (extension only) after live QA to strip
 * that fallback and drop emoji-picker-react from app.js.
 */
export const loadEmojiPicker = once(async (): Promise<any> => {
  try {
    const mod: any = await loadLazyComponentBundle("emoji-picker");
    return mod.EmojiPickerComponent ?? mod.default;
  } catch (e) {
    if (__BT_EMOJI_LAZY_FALLBACK__) {
      console.warn("[lazy-lib] lazy/emoji-picker.js load failed — using bundled fallback", e);
      const mod: any = await import(/* webpackMode: "eager" */ "emoji-picker-react");
      return mod.default ?? mod;
    }
    throw e;
  }
});
